// src/components/result/ExertionResultPreview.js
import React, { useMemo, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ResultPreview } from "./ResultPreview";
import LoadingBouncyDots from "../ui/LoadingBouncyDots";
import MetricLineChart from "../charts/MetricLineChart";
import { useAccessControl } from "../../hooks/useAccessControl";
import { ConfirmModal } from "../ui/ConfirmModal";
import { useUserDoc } from "../../hooks/useUserDoc";
import {
  getExertionReportOrder,
  createExertionReportOrder,
} from "../../utils/firebase/exertionReports";
import {
  computeRecoveryMetricsChosen,
  chooseRecoveryModel,
  secondsToMinSec,
} from "../../utils/exertion/recovery";

export function ExertionResultPreview({
  activityId,
  date,
  data,
  locked,
  loading = false,
}) {
  const { t } = useTranslation();
  const { rules } = useAccessControl();
  const { user } = useUserDoc();

  // report order state for this activity
  const [order, setOrder] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.uid || !activityId) return;
      const existing = await getExertionReportOrder(user.uid, activityId);
      if (!cancelled) setOrder(existing);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.uid, activityId]);

  const dot = (
    <div className="p-1">
      <LoadingBouncyDots
        text=""
        size={4}
        gap={1}
        speed={720}
        className="items-start"
        textClassName="hidden"
      />
    </div>
  );

  const pricePence = 500;
  const currency = "GBP";

  const { fitDataset, xMax } = useMemo(() => {
    const chosen = chooseRecoveryModel(data);
    const series = chosen.series || [];
    const xmax = series.length
      ? Math.max(15, Math.ceil(series[series.length - 1].x))
      : 60;
    const ds = series.length
      ? {
          label: t("activity.exertion.fitLabel", {
            defaultValue: "Recovery Curve",
          }),
          data: series,
          borderWidth: 3,
          tension: 0.25,
          pointRadius: 0,
        }
      : null;
    return { fitDataset: ds, xMax: xmax };
  }, [data, t]);

  const stats = useMemo(() => {
    if (loading) {
      return [
        {
          label: t("activity.exertion-recovery.stat2", {
            defaultValue: "Time",
          }),
          value: dot,
          hint: t("common.processing", { defaultValue: "Processing" }),
          metric: "duration",
        },
        {
          label: t("activity.exertion-recovery.stat4", {
            defaultValue: "Resp. Rate",
          }),
          value: dot,
          hint: t("common.processing", { defaultValue: "Processing" }),
          metric: "respiratoryRate",
        },
        {
          label: t("metrics.breathingRecovery60.subname", {
            defaultValue: "W60",
          }),
          value: dot,
          hint: t("common.processing", { defaultValue: "Processing" }),
          metric: "breathingRecovery",
        },
        {
          label: t("metrics.recoveryTime95.subname", {
            defaultValue: "Rec. Time (95%)",
          }),
          value: dot,
          hint: t("common.processing", { defaultValue: "Processing" }),
          metric: "recoveryTime",
        },
      ];
    }

    const d = data || {};
    const recovery = computeRecoveryMetricsChosen(d);
    const rr = Number.isFinite(d?.estimated_rr_bpm)
      ? Math.round(d.estimated_rr_bpm)
      : null;
    const durationSec = Number.isFinite(d?.duration_s_full)
      ? Math.round(d.duration_s_full)
      : null;

    const w60 =
      recovery.ok && isFinite(recovery.rrRecovery?.w60?.drop)
        ? Math.round(recovery.rrRecovery.w60.drop * 100) / 100
        : null;

    const t95s =
      recovery.ok && isFinite(recovery.timeToBaseline?.seconds95)
        ? Math.max(0, recovery.timeToBaseline.seconds95)
        : null;
    const t95Label = Number.isFinite(t95s) ? secondsToMinSec(t95s).label : "";

    const mmss = (s) =>
      Number.isFinite(s)
        ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`
        : "";

    return [
      {
        label: t("activity.exertion-recovery.stat2", { defaultValue: "Time" }),
        value: durationSec != null ? mmss(durationSec) : dot,
        hint: t("activity.exertion-recovery.stat2hint", {
          defaultValue: "Test",
        }),
        metric: "duration",
      },
      {
        label: t("activity.exertion-recovery.stat4", {
          defaultValue: "Resp. Rate",
        }),
        value: Number.isFinite(rr) ? String(rr) : dot,
        hint: t("activity.exertion-recovery.stat4hint", {
          defaultValue: "Test",
        }),
        metric: "respiratoryRate",
      },
      {
        label: t("metrics.breathingRecovery60.subname", {
          defaultValue: "W60",
        }),
        value: Number.isFinite(w60) ? String(w60) : dot,
        hint: t("metrics.breathingRecovery60.unit", { defaultValue: "s" }),
        metric: "breathingRecovery",
      },
      {
        label: t("metrics.recoveryTime95.subname", {
          defaultValue: "Rec. Time (95%)",
        }),
        value: t95Label || dot,
        hint: t("metrics.recoveryTime95.unit", { defaultValue: "s" }),
        metric: "recoveryTime",
      },
    ];
  }, [loading, data, t]);

  const chart = (
    <MetricLineChart
      height={160}
      datasets={fitDataset ? [fitDataset] : []}
      preset="none"
      showLegend={false}
      showTooltip={false}
      xType="linear"
      xMin={0}
      xMax={xMax ?? 60}
      xTickStep={60}
      xTickFormat={(v) => `${v}s`}
      yDecimals={1}
    />
  );

  const handleOpenModal = useCallback(() => {
    setModalOpen(true);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!user?.uid || !activityId) return;
    try {
      setBusy(true);
      const payload = await createExertionReportOrder({
        uid: user.uid,
        activityId,
        price: pricePence,
        currency,
        activityDateMs: date ? new Date(date).getTime() : null,
        resultRef: data?.resultRef ?? null,
      });
      setOrder(payload);
      setModalOpen(false);
    } catch (e) {
      // optional: toast error
    } finally {
      setBusy(false);
    }
  }, [user?.uid, activityId, pricePence, currency, date, data?.resultRef]);

  const handleCancel = useCallback(() => setModalOpen(false), []);

  // ---- NEW: prefer custom title from data if present ----
  const hasCustomTitle =
    data && typeof data.title === "string" && data.title.trim().length > 0;

  const overlayVariant = order ? "comingSoon" : "locked";
  const overlayLabel = order
    ? t("pay.exertionReport.requested", { defaultValue: "Report requested" })
    : t("pay.exertionReport.cta", {
        defaultValue: "Buy Exertion Report (£5)",
      });
  const overlayAria = order
    ? t("pay.exertionReport.requestedAria", {
        defaultValue: "Report has been requested",
      })
    : t("pay.exertionReport.ctaAria", {
        defaultValue: "Buy an exertion recovery report",
      });

  return (
    <>
      <ResultPreview
        activityDate={date}
        stats={stats}
        locks={rules?.locks ?? {}}
        showLockedUI={false}
        // If there is a custom title, skip i18n key and just use it as default text.
        titleKey={
          hasCustomTitle
            ? undefined
            : "activity.exertion-recovery.exertion.title"
        }
        titleDefault={hasCustomTitle ? data.title : "Exertion Test"}
        chartTitle={t("activity.exertion-recovery.chartTitle", {
          defaultValue: "Recovery Curve",
        })}
        chart={chart}
        infoKey="info.recoveryCurve"
        cta={{
          label: overlayLabel,
          ariaLabel: overlayAria,
        }}
        lockProps={{ variant: overlayVariant }}
        onUnlock={order ? undefined : handleOpenModal}
      />

      <ConfirmModal
        open={modalOpen}
        title={t("pay.modal.exertionReport.title", {
          defaultValue: "Confirm Exertion Recovery Report",
        })}
        description={t("pay.modal.exertionReport.desc", {
          defaultValue:
            "We’ll prepare a personalised report based on this test and email it to you alongside an invoice.",
        })}
        confirmLabel={t("pay.modal.exertionReport.confirm", {
          defaultValue: "Confirm (£5)",
        })}
        cancelLabel={t("common.cancel", { defaultValue: "Cancel" })}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        busy={busy}
      />
    </>
  );
}
